import React, { Component } from "react";
import _ from "underscore";
import PropTypes from "prop-types";
import CDCModalInfo from "./cdc-modal-info";
import CDCModalInput from "./cdc-modal-input";
import CDCAddressForm from "./cdc-address-form";
import CDCAddressDisplay from "./cdc-address-display";
import { AdvancedCombobox, Loading } from "@evertrue/et-components";
import Decorator from "@evertrue/client-decorator";
import { WithCDCDependencies } from "../contexts/cdc-dependencies-context";

const ADDRESS_REQUIRED_FIELDS = ["address_1", "city", "state", "zip_code", "country"];

const validateEmail = email => {
  const regex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(email);
};

const isInvalid = (type, value) => {
  if (type === "email") {
    return value.length > 0 && !validateEmail(value.trim());
  } else if (type === "phone") {
    const digits = value.match(/\d/g);
    if (digits && value.length > 0) {
      return digits.length < 10;
    } else if (value.length === 0) {
      return false;
    } else return true;
  } else if (type === "address") {
    return !_.every(ADDRESS_REQUIRED_FIELDS, field => {
      return _.has(value, field);
    });
  }
  return false;
};

const checkForDisabled = (propsVal, stateVal, panelStatus, type) => {
  if (panelStatus !== "retire") {
    if (typeof propsVal === "string" && typeof stateVal === "string") {
      return _.isEqual(propsVal.trim(), stateVal.trim()) || stateVal === "" || isInvalid(type, stateVal);
    } else {
      return _.isEqual(propsVal, stateVal) || stateVal === "" || isInvalid(type, stateVal);
    }
  } else return false;
};

const OPTIONS = [
  { label: "Home", value: "home" },
  { label: "Work", value: "work" },
  { label: "Other", value: "other" },
];

class CDCModalRow extends Component {
  static propTypes = {
    contact: PropTypes.object,
    user: PropTypes.object, // comes from WithCDCUtils HOC
    id: PropTypes.string,
    dataType: PropTypes.oneOf(["phone", "email", "address", "misc"]),
    valueDescription: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    label: PropTypes.string,
    placeholder: PropTypes.string,
    panelStatus: PropTypes.oneOf(["view", "edit", "retire", "new"]),
    onEdit: PropTypes.func,
    onRetire: PropTypes.func,
    onSuccess: PropTypes.func,
    onFailure: PropTypes.func,
    onCancel: PropTypes.func,
    isActive: PropTypes.bool,
    dependencies: PropTypes.object.isRequired,
  };

  static defaultProps = {
    contact: {},
    user: {},
    id: "",
    dataType: "",
    valueDescription: "home",
    value: "",
    label: "",
    panelStatus: "view",
    placeholder: "",
    onEdit: _.noop,
    onRetire: _.noop,
    onSuccess: _.noop,
    onFailure: _.noop,
    onCancel: _.noop,
    isActive: false,
  };

  state = {
    value: this.props.value,
    valueDescription: this.props.valueDescription,
    loading: false,
  };

  handleFormChange = value => {
    this.setState({ value });
  };

  handleCancel = () => {
    this.setState({ value: this.props.value });
    this.props.onCancel();
  };

  getFormerProperties = () => {
    if (this.props.dataType === "address") {
      return { type: this.props.valueDescription, ...this.props.value };
    }
    return { type: this.state.valueDescription, [this.props.dataType]: this.props.value };
  };

  getNewProperties = () => {
    if (this.props.dataType === "address") {
      return { type: this.state.valueDescription, ...this.state.value };
    }
    if (this.props.dataType === "misc") {
      return this.state.value;
    }
    return { type: this.state.valueDescription, [this.props.dataType]: this.state.value };
  };

  getContactRemoteId = () => {
    const remoteId = _.findWhere(this.props.contact.identities, { type: "remote_id" });
    if (remoteId) return remoteId.value;
  };

  generateEditQuery = note => {
    return {
      contact_remote_id: this.getContactRemoteId(),
      contact_id: this.props.contact.id,
      contact_name: Decorator.Contacts.getFullName(this.props.contact),
      submitter_user_name: this.props.dependencies.user.name,
      suggestions: [
        {
          data_type: this.props.dataType,
          former_properties: this.getFormerProperties(),
          new_properties: this.getNewProperties(),
        },
      ],
      note,
    };
  };

  generateMiscQuery = note => {
    return {
      contact_remote_id: this.getContactRemoteId(),
      contact_id: this.props.contact.id,
      contact_name: Decorator.Contacts.getFullName(this.props.contact),
      submitter_user_name: this.props.dependencies.user.name,
      note,
      suggestions: [{ other: this.getNewProperties() }],
    };
  };

  generateRetireQuery = note => {
    return {
      contact_remote_id: this.getContactRemoteId(),
      contact_id: this.props.contact.id,
      contact_name: Decorator.Contacts.getFullName(this.props.contact),
      submitter_user_name: this.props.dependencies.user.name,
      suggestions: [
        {
          data_type: this.props.dataType,
          former_properties: this.getFormerProperties(),
        },
      ],
      note,
    };
  };

  generateNewQuery = note => {
    return {
      contact_remote_id: this.getContactRemoteId(),
      contact_id: this.props.contact.id,
      contact_name: Decorator.Contacts.getFullName(this.props.contact),
      submitter_user_name: this.props.dependencies.user.name,
      suggestions: [
        {
          data_type: this.props.dataType,
          new_properties: this.getNewProperties(),
        },
      ],
      note,
    };
  };

  handleSubmit = note => {
    let query;
    const { dataType, panelStatus, value } = this.props;
    if (dataType === "misc") {
      query = this.generateMiscQuery(note);
    } else {
      if (panelStatus === "retire") {
        query = this.generateRetireQuery(note);
      } else if (panelStatus === "new") {
        query = this.generateNewQuery(note);
      } else {
        query = this.generateEditQuery(note);
      }
    }

    this.setState({ loading: true });

    this.props.dependencies
      .submitSuggestion({
        data: query,
      })
      .then((resp = {}) => {
        this.setState({ value: value, loading: false });
        this.props.onSuccess(dataType, panelStatus);
      })
      .catch(() => {
        this.setState({ loading: false });
        this.props.onFailure();
      });
  };

  renderTypeForm = () => {
    const { panelStatus, isActive, dataType } = this.props;
    if (panelStatus === "new" && isActive && dataType !== "misc") {
      return (
        <div className="cdc-controller--row-new">
          <label>Type*</label>
          <AdvancedCombobox
            options={OPTIONS}
            value={_.findWhere(OPTIONS, { value: this.state.valueDescription })}
            onChange={value => this.setState({ valueDescription: value.value })}
          />
        </div>
      );
    }
  };

  renderForm = () => {
    const { dataType, placeholder, panelStatus } = this.props;
    const { value } = this.state;
    if (dataType === "address") {
      return (
        <CDCAddressForm
          dataType={dataType}
          panelStatus={panelStatus}
          address={value}
          handleChange={this.handleFormChange}
        />
      );
    }
    return (
      <CDCModalInput
        dataType={dataType}
        value={value}
        placeholder={placeholder}
        isInvalid={isInvalid(dataType, value)}
        isRequired
        handleChange={this.handleFormChange}
      />
    );
  };

  renderDisplay = () => {
    const { dataType, value } = this.props;
    return dataType === "address" ? <CDCAddressDisplay address={value} /> : value;
  };

  renderData = () => {
    const { panelStatus, isActive } = this.props;
    const showForm = panelStatus === "edit" || panelStatus === "new";
    if (this.state.loading) {
      return <Loading />;
    } else if (isActive && showForm) return this.renderForm();
    return this.renderDisplay();
  };

  renderActions = () => {
    if (this.props.panelStatus === "view") {
      return (
        <div className="cdc-controller--actions">
          <a className="cdc-controller--edit-link" onClick={() => this.props.onEdit(this.props.id)}>
            Edit
          </a>
          <a onClick={() => this.props.onRetire(this.props.id)}>Retire</a>
        </div>
      );
    }
  };

  renderInfo = () => {
    const { value, isActive, panelStatus, dataType } = this.props;
    if (isActive) {
      return (
        <CDCModalInfo
          dataType={dataType}
          isDisabled={checkForDisabled(value, this.state.value, panelStatus, dataType)}
          panelStatus={panelStatus}
          onCancel={this.handleCancel}
          onSubmit={this.handleSubmit}
        />
      );
    }
  };

  render() {
    const { isActive, label } = this.props;
    return (
      <div className="cdc-controller--row">
        <label className={"cdc-controller-row--label " + isActive ? "cdc-controller-row--label-active" : ""}>
          {label}
        </label>
        {this.renderTypeForm()}
        <div className="cdc-controller--value">
          {this.renderData()}
          {this.renderActions()}
        </div>
        {this.renderInfo()}
      </div>
    );
  }
}

export default WithCDCDependencies(CDCModalRow);
